import "./multi-feed-tease-row.scss";

import { ArticleTeaseProps } from "design-system/components/primitives/article-tease/article-tease";
import {
  ComponentHeader,
  ComponentHeaderProps,
} from "design-system/components/primitives/component-header/component-header";
import { CtaLink } from "design-system/components/primitives/cta-link/cta-link";
import { TeaseFeed } from "design-system/components/blocks/tease-feed/tease-feed";
import { EditAttributes } from "design-system/types/types";

interface CTA {
  link?: string;
  linkText?: string;
  ariaLabel?: string;
}

export interface MultiFeedTeaseColumn {
  header?: ComponentHeaderProps;
  articles: Array<ArticleTeaseProps>;
  cta?: CTA;

  editAttributes?: {
    cta?: EditAttributes;
    title?: EditAttributes;
    articles?: EditAttributes;
  };
}

export interface MultiFeedTeaseRowProps {
  columns?: Array<MultiFeedTeaseColumn>;
  hasHeader?: boolean;
}

/**
 * Component for displaying multiple teases in a column format
 *
 * ## See it in use on...
 * - The [insights page](/story/example-pages-editorial-landing-pages-insights-page--story)
 *
 *
 * - **`id: CB-011-000-00`**
 * - **`data-region: cb__multi-feed-tease-row`**
 */
export function MultiFeedTeaseRow({
  columns,
  hasHeader,
}: MultiFeedTeaseRowProps) {
  if (!Array.isArray(columns)) return null;

  return (
    <div
      className="hbs-global-align-full hbs-component--multi-feed-tease-row"
      data-region="cb__multi-feed-tease-row"
    >
      <div className="hbs-multi-feed-tease-row">
        {columns.map(({ header, cta, articles, editAttributes }, i) => (
          <div className="hbs-multi-feed-tease-row__feed" key={i}>
            {header && (
              <ComponentHeader
                editAttributes={{ title: editAttributes?.title }}
                {...header}
                HeadingLevel={hasHeader ? "h3" : "h2"}
              />
            )}

            <div
              {...editAttributes?.articles}
              className="hbs-multi-feed-tease-row__articles"
            >
              <TeaseFeed
                items={articles.map((props) => ({
                  name: "ArticleTease",
                  props,
                }))}
                teaseStyle={"compressed"}
                HeadingLevelTease={
                  hasHeader ? (header ? "h4" : "h3") : header ? "h3" : "h2"
                }
              />
            </div>

            {cta && (
              <div
                {...editAttributes?.cta}
                className="hbs-multi-feed-tease-row__cta"
              >
                <CtaLink isSmall href={cta.link} aria-label={cta.ariaLabel}>
                  {cta.linkText}
                </CtaLink>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
